import { useMemo } from 'react';
import { BLING_HIDDEN_DEPOSIT_IDS } from '../../utils/blingStockConfig';

function toNumericId(value) {
    const parsed = Number.parseInt(String(value || ''), 10);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Tradução de status da ML pra rótulos legíveis em PT-BR. Acompanha o que a API
 * `/inventories/:id/stock/fulfillment` devolve em `not_available_detail[].status`.
 */
const ML_STATUS_LABEL = {
    damaged: 'Corrompido',
    lost: 'Perdido',
    withdrawal: 'Reservado para retirada',
    internalProcess: 'Processo interno',
    transfer: 'Em transferência',
    noFiscalCoverage: 'Sem cobertura fiscal',
    not_supported: 'Não suportado',
};

/**
 * Painel da aba Estoque do DetailPanel — tabela 3 colunas:
 *   Provedor (Bling/ML) | Depósito | Quantidade
 *
 * Subtotal por provedor + total geral. Substituiu a versão antiga com cards
 * verticais grandes (cada warehouse virava um cartão com ícone Storefront) —
 * a tabela ocupa muito menos espaço vertical e é mais escaneável.
 *
 * Linhas Bling escondidas via BLING_HIDDEN_DEPOSIT_IDS são filtradas (mesmos
 * depósitos que o /stock também esconde por config).
 */
const StockBreakdownPanel = ({
    warehouses = [],
    warehouseNameById = new Map(),
    mlStockData = null,
    loading = false,
}) => {
    const blingRows = useMemo(() => {
        return (warehouses || [])
            .map((w) => {
                const id = toNumericId(w?.id ?? w?.reference_id);
                const name =
                    (Number.isFinite(id) ? warehouseNameById.get(id) : null) ||
                    warehouseNameById.get(String(w?.id || w?.reference_id || '')) ||
                    w?.name ||
                    w?.descricao ||
                    `Depósito ${id || '-'}`;
                return {
                    id,
                    code: Number.isFinite(id) ? String(id) : String(w?.reference_id || w?.id || ''),
                    name,
                    quantity: Number(w?.quantity || 0),
                };
            })
            // `quantity !== 0` (não `> 0`) — depósitos com saldo NEGATIVO são
            // erros de cadastro Bling que o user precisa investigar. Esconder
            // virava "Nenhum depósito com saldo" mesmo quando havia -2 unidades.
            .filter((r) => r.quantity !== 0 && !BLING_HIDDEN_DEPOSIT_IDS.includes(r.id));
    }, [warehouses, warehouseNameById]);

    const blingSubtotal = blingRows.reduce((s, r) => s + r.quantity, 0);

    const mlTotal = Number(mlStockData?.total ?? 0);
    const mlAvailable = Number(mlStockData?.available_quantity ?? 0);
    const mlInventoryId = mlStockData?.inventory_id || null;

    // `mlUnavailable` é derivado DENTRO do useMemo pra evitar nova array a cada
    // render (que invalidaria `mlRows` desnecessariamente).
    const mlRows = useMemo(() => {
        const unavailable = Array.isArray(mlStockData?.not_available_detail)
            ? mlStockData.not_available_detail.filter((d) => Number(d?.quantity || 0) > 0)
            : [];
        const out = [];
        if (mlAvailable > 0) {
            out.push({
                code: mlInventoryId || 'available',
                name: 'Disponível',
                quantity: mlAvailable,
            });
        }
        for (const d of unavailable) {
            out.push({
                code: d.status,
                name: ML_STATUS_LABEL[d.status] || d.status,
                quantity: Number(d.quantity || 0),
            });
        }
        return out;
    }, [mlAvailable, mlInventoryId, mlStockData?.not_available_detail]);

    const hasData = blingRows.length > 0 || mlRows.length > 0;
    const grandTotal = blingSubtotal + mlTotal;

    if (loading) {
        return (
            <div className="py-6 text-center text-sm text-mono-500 dark:text-mono-400">
                Carregando depósitos…
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className="py-6 text-center text-sm text-mono-500 dark:text-mono-400">
                Nenhum depósito com saldo.
            </div>
        );
    }

    // Estilo unificado pra cells. Mantém alinhamento e padding constantes
    // entre header/body/footer.
    const thBase =
        'px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400';
    const tdBase = 'px-3 py-2 text-sm text-mono-800 dark:text-mono-200';
    const subtotalRowClass =
        'border-t border-mono-200 dark:border-mono-700 bg-mono-50 dark:bg-mono-950/50 font-medium';

    return (
        // table-fixed pra que as larguras das `th` sejam respeitadas; a coluna
        // do meio fica com o resto e faz ellipsis se o nome longo passar. Sem
        // table-fixed o auto-layout estica colunas ignorando o w-[...] das th.
        // Provedor: 110px ("Mercado Livre" cabe sem ser sobreposto pela coluna
        // de Depósitos/Inventários, antes era 80px e truncava).
        <table className="w-full border-collapse text-sm" style={{ tableLayout: 'fixed' }}>
            <thead>
                <tr className="border-b border-mono-200 dark:border-mono-800">
                    <th className={`${thBase} w-[110px]`}>Provedor</th>
                    <th className={thBase}>Depósitos/Inventários</th>
                    <th className={`${thBase} text-right w-[110px]`}>Quantidade</th>
                </tr>
            </thead>
            <tbody>
                {blingRows.map((row) => (
                    <tr
                        key={`bling-${row.code}`}
                        className="border-b border-mono-100 dark:border-mono-900"
                    >
                        {/* Cor #288F4E = verde da identidade visual do Bling. */}
                        <td className={tdBase} style={{ color: '#288F4E', fontWeight: 600 }}>Bling</td>
                        <td className={tdBase}>
                            <span className="font-medium">{row.name}</span>
                            <span className="ml-2 font-mono text-xs text-mono-500 dark:text-mono-500">
                                {row.code}
                            </span>
                        </td>
                        <td className={`${tdBase} text-right font-mono tabular-nums`}>
                            {row.quantity}
                        </td>
                    </tr>
                ))}
                {blingRows.length > 0 && (
                    <tr className={subtotalRowClass}>
                        <td colSpan={2} className={`${tdBase} text-right`}>
                            Subtotal
                        </td>
                        <td className={`${tdBase} text-right font-mono tabular-nums`}>
                            {blingSubtotal}
                        </td>
                    </tr>
                )}

                {mlRows.map((row) => (
                    <tr
                        key={`ml-${row.code}`}
                        className="border-b border-mono-100 dark:border-mono-900"
                    >
                        {/* Cor #ffe600 = amarelo oficial do Mercado Livre.
                            Aplicado SÓ no texto (sem background) pra ficar consistente
                            com a linha Bling, que também usa só cor. */}
                        {/* whiteSpace:nowrap em TODAS as células da linha ML — o overflow
                            é cortado no <table> via display:block; assim a row inteira
                            fica em 1 linha mesmo com nome longo. Tentativas anteriores
                            só tinham nowrap na célula de nome — a cor "Mercado Livre"
                            também quebrava se o td não tivesse nowrap. */}
                        <td className={tdBase} style={{ color: '#ffe600', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            Mercado Livre
                        </td>
                        <td className={tdBase} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <span className="font-medium">{row.name}</span>
                            {row.code !== row.name && (
                                <span className="ml-2 font-mono text-xs text-mono-500 dark:text-mono-500">
                                    {row.code}
                                </span>
                            )}
                        </td>
                        <td className={`${tdBase} text-right font-mono tabular-nums`}>
                            {row.quantity}
                        </td>
                    </tr>
                ))}
                {mlRows.length > 0 && (
                    <tr className={subtotalRowClass}>
                        <td colSpan={2} className={`${tdBase} text-right`}>
                            Subtotal
                        </td>
                        <td className={`${tdBase} text-right font-mono tabular-nums`}>{mlTotal}</td>
                    </tr>
                )}
            </tbody>
            <tfoot>
                <tr className="border-t-2 border-mono-300 dark:border-mono-700">
                    <td colSpan={2} className={`${tdBase} text-right font-semibold`}>
                        Total geral
                    </td>
                    <td className={`${tdBase} text-right font-mono font-bold tabular-nums`}>
                        {grandTotal}
                    </td>
                </tr>
            </tfoot>
        </table>
    );
};

export default StockBreakdownPanel;
